import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const ManageCategories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);

    const [formData, setFormData] = useState({
        category_name: '',
        top_category: 0,
        description: ''
    });

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const response = await api.get('/categories');
            setCategories(response.data.data || response.data);
        } catch (error) {
            toast.error('Failed to load categories');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const payload = {
            category_name: formData.category_name,
            top_category: formData.top_category,  // Remove Number() conversion
            description: formData.description
        };

        try {
            if (editingCategory) {
                // Add proper headers
                const response = await api.put(`/admin/categories/${editingCategory.id}`, payload, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                });

                console.log('Update response:', response.data); // Debug log
                toast.success('Category updated successfully');
            } else {
                await api.post('/admin/categories', payload);
                toast.success('Category created successfully');
            }

            handleCloseModal();
            fetchCategories();
        } catch (error) {
            console.error('Error details:', error.response); // Debug log
            handleCloseModal();
            toast.error(error.response?.data?.message || 'Failed to save category');
        }
    };

    const handleEdit = (category) => {
        setEditingCategory(category);
        setFormData({
            category_name: category.category_name,
            top_category: Number(category.top_category),
            description: category.description || ''
        });
        setShowModal(true);
    };

    const handleDelete = async (categoryId) => {
        const result = await Swal.fire({
            title: 'Delete Category',
            text: 'Are you sure you want to delete this category?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            await api.delete(`/admin/categories/${categoryId}`);

            toast.success("Category deleted successfully!");
            fetchCategories();

        } catch (error) {
            toast.error("Failed to delete category");
        }
    };


    const handleCloseModal = () => {
        setShowModal(false);
        setEditingCategory(null);
        setFormData({
            category_name: '',
            top_category: 0,
            description: ''
        });
    };


    return (
        <div className="container-fluid">
            <div className="d-sm-flex align-items-center justify-content-between mb-4">
                <h1 className="h3 mb-0 text-gray-900 font-weight-bold">Manage Categories</h1>

                <button
                    className="btn btn-sm btn-primary"
                    onClick={() => setShowModal(true)}
                >
                    <i className="fas fa-plus mr-2"></i>
                    Add Category
                </button>
            </div>

            {/* Categories Grid */}
            <div className="row">
                {loading ? (
                    <div className="col-12 text-center py-8">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                ) : (
                categories.map(category => (
                    <div key={category.id} className="col-xl-3 col-md-6 mb-4">
                        <div className="card border-left-primary shadow h-100 py-2">
                            <div className="card-body">
                                <div className="row no-gutters align-items-center">
                                    <div className="col mr-2">
                                        <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                                            {category.category_name}
                                        </div>
                                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                                            {category.events_count || 0} events
                                        </div>

                                        {category.description && (
                                            <div className="text-xs text-gray-600 mt-2">
                                                {category.description}
                                            </div>
                                        )}

                                        <div className="text-xs mt-2">
                                            <strong>Top:</strong> {category.top_category ? "Yes" : "No"}
                                        </div>
                                    </div>

                                    <div className="col-auto">
                                        <i className="fas fa-calendar fa-2x text-gray-300"></i>
                                    </div>
                                </div>

                                <div className="mt-3 d-flex gap-2">
                                    <button
                                        onClick={() => handleEdit(category)}
                                        className="p-2 text-info rounded transition-colors border-0 bg-transparent"
                                        style={{ background: 'transparent' }}
                                        title="Edit Category"
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#cfe2ff'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                        <i className="fas fa-edit"></i>
                                    </button>

                                    <button
                                        onClick={() => handleDelete(category.id)}
                                        className="p-2 text-danger rounded transition-colors border-0 bg-transparent"
                                        style={{ background: 'transparent' }}
                                        title="Delete Category"
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8d7da'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                        <i className="fas fa-trash"></i>
                                    </button>
                                </div>

                            </div>
                        </div>
                    </div>
                )) )}
            </div>


            {/* Modal */}
            {showModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">

                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editingCategory ? 'Edit Category' : 'Add New Category'}
                                </h5>
                                <button type="button" className="close" onClick={handleCloseModal}>
                                    <span>&times;</span>
                                </button>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">

                                    <div className="form-group">
                                        <label>Category Name *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.category_name}
                                            onChange={(e) => setFormData({ ...formData, category_name: e.target.value })}
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Top Category *</label>
                                        <select
                                            className="form-control"
                                            value={formData.top_category}
                                            onChange={(e) => setFormData({ ...formData, top_category: Number(e.target.value) })}
                                        >
                                            <option value={0}>No</option>
                                            <option value={1}>Yes</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Description</label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        />
                                    </div>

                                </div>

                                <div className="modal-footer">
                                    <button type="submit" className="btn btn-sm btn-success">
                                        {editingCategory ? 'Update' : 'Create'} Category
                                    </button>
                                </div>

                            </form>

                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageCategories;
